import { useSearchUsersQuery, type User } from '@paintscout/api'
import type { DialogProps } from '@paintscout/ui'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Spinner } from '@paintscout/ui'
import { UserTiles, useUsers } from '@paintscout/ui-business'
import { addAuth0Prefix, canBeEstimator, removeAuth0Prefix } from '@paintscout/util/users'
import React, { useMemo, useState } from 'react'

export interface ChangeEstimatorDialogProps extends DialogProps {
  onConfirm?: (user?: User) => void
  onCancel?: () => void
  /**
   * Scopes the estimator list to a specific company's users, e.g. when a superadmin/support user
   * administers a company from outside its own ClientOptionsProvider (ENG-1557) — in that case the
   * shared UsersProvider's clientId isn't available, so this dialog queries directly instead.
   */
  queryOverride?: string
  currentEstimatorId?: string
}

/**
 * Select new estimator for a quote, this is user facing user search, non-admin version
 * @returns User if found, null if none selected
 */
function ChangeEstimatorDialog(props: ChangeEstimatorDialogProps) {
  const { onCancel, onConfirm, currentEstimatorId = '', queryOverride, ...baseDialogProps } = props
  const { users: contextUsers, loading: contextLoading } = useUsers()
  const { data: overrideData, loading: overrideLoading } = useSearchUsersQuery({
    variables: { query: queryOverride, includeInactive: true },
    skip: !queryOverride,
    fetchPolicy: 'cache-and-network'
  })

  const users = queryOverride ? overrideData?.searchUsers?.rows ?? [] : contextUsers
  const loading = queryOverride ? overrideLoading && !overrideData : contextLoading
  const [selectedEstimator, setSelectedEstimator] = useState(
    currentEstimatorId ? addAuth0Prefix(currentEstimatorId) : ''
  )

  const initialEstimator = useMemo(
    () => users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(currentEstimatorId)),
    [users, currentEstimatorId]
  )

  return (
    <Dialog open={open} maxWidth="md" {...baseDialogProps}>
      <DialogTitle>Change Estimator</DialogTitle>
      <DialogContent>
        {loading && !users.length ? (
          <Spinner fullWidth fullHeight />
        ) : (
          <UserTiles
            // always keep initial owner at beginning of list so it's not buried further down the dialog
            allowNone={true}
            users={[
              ...(initialEstimator ? [initialEstimator] : []),
              ...users.filter(
                (user) =>
                  removeAuth0Prefix(user.user_id) !== removeAuth0Prefix(initialEstimator?.user_id ?? '') &&
                  user.app_metadata?.active &&
                  // Only Sales-seat users can own a document — Team and Painter seats are out
                  // (ENG-1711). The initial estimator is prepended above, so an older assignment
                  // to another seat still shows.
                  canBeEstimator(user)
              )
            ]}
            selectedUserIds={selectedEstimator ? [selectedEstimator] : []}
            onUserSelect={(user) => {
              setSelectedEstimator(user?.user_id ?? null)
            }}
          />
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
      >
        <Button
          type="submit"
          variant={'contained'}
          onClick={() => {
            const selectedUser = selectedEstimator
              ? users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(selectedEstimator))
              : null
            onConfirm(selectedUser ?? null)
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ChangeEstimatorDialog
